import { api } from '../client';

import type { ListResponse, PaginationParams } from './common';
import {
  rememberResponseItemsUpdatedAt,
  rememberResponseUpdatedAt,
  type StockDocumentMutationDeps,
  type StockDocumentMutationOptions,
} from './documentMutationCache';

export type RecipeUnitCode = 'pcs' | 'portion' | 'kg' | 'l';

export interface RecipeIngredientPreview {
  ingredient_product_id: string;
  product_name?: string | null;
  quantity: string;
  unit_code: RecipeUnitCode;
  has_active_recipe: boolean;
  active_recipe_id?: string | null;
}

export interface RecipeListItem {
  id: string;
  product_id: string;
  product_name?: string | null;
  product_category_name?: string | null;
  product_ownership?: string | null;
  name: string;
  yield_quantity: string;
  yield_unit_code: RecipeUnitCode;
  is_active: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
  ingredients_count: number;
  has_sub_recipes: boolean;
  ingredients_preview: RecipeIngredientPreview[];
  cost_per_unit?: string | null;
  labor_cost_per_unit?: string | null;
  overhead_cost_per_unit?: string | null;
  total_cost_per_unit?: string | null;
  canonical_price?: string | null;
  tax_rate?: string | null;
  is_partial: boolean;
}

export interface RecipeListSummary {
  by_status: Record<string, number>;
}

export interface RecipeCategoryGroupsResponse {
  request_id: string;
  intermediate: string[];
  product: string[];
}

export interface RecipesResponse extends ListResponse<RecipeListItem> {
  summary?: RecipeListSummary | null;
}

export interface RecipeIngredient {
  id: string;
  recipe_id: string;
  ingredient_product_id: string;
  ingredient_product_name?: string | null;
  ingredient_product_is_active?: boolean | null;
  quantity: string;
  unit_code: RecipeUnitCode;
  has_active_recipe: boolean;
  active_recipe_id?: string | null;
}

export interface Recipe {
  id: string;
  product_id: string;
  product_name?: string | null;
  product_ownership?: string | null;
  name: string;
  yield_quantity: string;
  yield_unit_code: RecipeUnitCode;
  yield_weight_kg?: string | null;
  is_active: boolean;
  created_at?: string | null;
  updated_at?: string | null;
  updated_by?: string | null;
  ingredients: RecipeIngredient[];
}

export interface RecipeDetailResponse {
  request_id: string;
  recipe: Recipe;
}

export interface RecipeFilters extends PaginationParams {
  is_active?: boolean;
  search?: string;
  product_id?: string;
  include_summary?: boolean;
}

export interface RecipeIngredientRequest {
  ingredient_product_id: string;
  quantity: string;
  unit_code: RecipeUnitCode;
}

export interface CreateRecipeRequest {
  product_id: string;
  name: string;
  yield_quantity: string;
  yield_unit_code: RecipeUnitCode;
  yield_weight_kg?: string | null;
  ingredients: RecipeIngredientRequest[];
}

export interface UpdateRecipeRequest {
  name: string;
  yield_quantity: string;
  yield_unit_code: RecipeUnitCode;
  yield_weight_kg?: string | null;
  ingredients: RecipeIngredientRequest[];
}

export interface RecipePreviewRequest {
  product_id: string;
  yield_quantity: string;
  yield_unit_code: RecipeUnitCode;
  yield_weight_kg?: string | null;
  ingredients: RecipeIngredientRequest[];
}

export interface RecipeDirectIngredientCost {
  ingredient_product_id: string;
  cost_batch: string | null;
  unit_cost: string | null;
  is_computed: boolean;
  is_partial: boolean;
}

export interface RecipeTreeNode {
  product_id: string;
  recipe_id?: string | null;
  product_name?: string | null;
  quantity: string;
  unit_code: RecipeUnitCode;
  children: RecipeTreeNode[];
}

export interface RecipeFlatLeaf {
  ingredient_product_id: string;
  product_name?: string | null;
  quantity: string;
  unit_code: RecipeUnitCode;
  unit_cost: string | null;
  cost_batch: string | null;
}

export interface RecipePreviewSummary {
  total_cost_batch: string | null;
  total_cost_per_unit: string | null;
  is_partial: boolean;
  labor_rate_per_kg?: string | null;
  labor_cost_batch?: string | null;
  labor_cost_per_unit?: string | null;
  effective_weight_kg?: string | null;
  /** Production electricity and packaging per unit (finished products, owner 2026-10-04). */
  utilities_cost_per_unit?: string | null;
  packaging_cost_per_unit?: string | null;
  overhead_cost_per_unit?: string | null;
}

export interface RecipePreviewPayload {
  tree: RecipeTreeNode;
  flat_leaves: RecipeFlatLeaf[];
  summary: RecipePreviewSummary;
}

export interface RecipePreviewResponse {
  request_id: string;
  direct_ingredient_costs: Record<string, RecipeDirectIngredientCost>;
  preview: RecipePreviewPayload;
}

export interface RecipeFlattenIngredient {
  ingredient_product_id: string;
  product_name?: string | null;
  quantity_per_unit: string;
  unit_code: RecipeUnitCode;
}

export interface RecipeFlattenResponse {
  request_id: string;
  recipe_id: string;
  product_id: string;
  yield_unit_code: RecipeUnitCode;
  ingredients: RecipeFlattenIngredient[];
}

export interface ProductsWithRecipesResponse {
  request_id: string;
  product_ids: string[];
}

export interface RecipeUsedInItem {
  recipe_id: string;
  recipe_name: string;
  product_name: string | null;
  is_active: boolean;
  amount_per_unit: string | null;
  ingredient_unit_code: RecipeUnitCode;
  yield_unit_code: RecipeUnitCode;
}

export interface RecipeUsedInResponse {
  request_id: string;
  used_in: RecipeUsedInItem[];
}

export interface RecipeDeleteResponse {
  request_id: string;
  success: boolean;
}

export interface RecipeRecalculateProductionsRequest {
  from_date?: string | null;
}

export interface RecipeRecalculateProductionsResponse {
  request_id: string;
  success: boolean;
  replay?: boolean;
  recipe_id: string;
  affected_recipe_ids?: string[];
  from_date?: string | null;
  matched_count: number;
  revised_count: number;
  replayed_count: number;
  skipped_count: number;
  revised_production_ids: string[];
  replayed_production_ids: string[];
}

type RecipeStockEntity = 'recipe';

export type StockRecipesApiDeps = StockDocumentMutationDeps<RecipeStockEntity>;

export const createStockRecipesApi = ({
  rememberStockDocumentUpdatedAt,
  forgetStockDocumentUpdatedAt,
  withExpectedUpdatedAt,
}: StockRecipesApiDeps) => ({
  list: async (filters: RecipeFilters = {}): Promise<RecipesResponse> => {
    return rememberResponseItemsUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'recipe',
      await api.webAdmin.get<RecipesResponse>('/stock/recipes', { params: filters }),
      (response) => response.items,
      (item) => item.id,
      (item) => item.updated_at,
    );
  },

  detail: async (id: string): Promise<RecipeDetailResponse> => {
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'recipe',
      await api.webAdmin.get<RecipeDetailResponse>(`/stock/recipes/${id}`),
      (response) => response.recipe.id,
      (response) => response.recipe.updated_at,
    );
  },

  create: async (
    data: CreateRecipeRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<RecipeDetailResponse> => {
    const requestOptions = options?.idempotencyKey
      ? { idempotencyKey: options.idempotencyKey }
      : undefined;
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'recipe',
      await api.webAdmin.post<RecipeDetailResponse>('/stock/recipes', data, requestOptions),
      (response) => response.recipe.id,
      (response) => response.recipe.updated_at,
    );
  },

  update: async (
    id: string,
    data: UpdateRecipeRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<RecipeDetailResponse> => {
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'recipe',
      await api.webAdmin.put<RecipeDetailResponse>(
        `/stock/recipes/${id}`,
        data,
        withExpectedUpdatedAt('recipe', id, options),
      ),
      (response) => response.recipe.id,
      (response) => response.recipe.updated_at,
    );
  },

  activate: async (
    id: string,
    options?: StockDocumentMutationOptions,
  ): Promise<RecipeDetailResponse> => {
    const requestOptions = options?.idempotencyKey
      ? { idempotencyKey: options.idempotencyKey }
      : undefined;
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'recipe',
      await api.webAdmin.post<RecipeDetailResponse>(
        `/stock/recipes/${id}/activate`,
        undefined,
        requestOptions,
      ),
      (response) => response.recipe.id,
      (response) => response.recipe.updated_at,
    );
  },

  deactivate: async (
    id: string,
    options?: StockDocumentMutationOptions,
  ): Promise<RecipeDetailResponse> => {
    const requestOptions = options?.idempotencyKey
      ? { idempotencyKey: options.idempotencyKey }
      : undefined;
    return rememberResponseUpdatedAt(
      rememberStockDocumentUpdatedAt,
      'recipe',
      await api.webAdmin.post<RecipeDetailResponse>(
        `/stock/recipes/${id}/deactivate`,
        undefined,
        requestOptions,
      ),
      (response) => response.recipe.id,
      (response) => response.recipe.updated_at,
    );
  },

  delete: async (
    id: string,
    options?: StockDocumentMutationOptions,
  ): Promise<RecipeDeleteResponse> => {
    const requestOptions = options?.idempotencyKey
      ? { idempotencyKey: options.idempotencyKey }
      : undefined;
    const response = await api.webAdmin.delete<RecipeDeleteResponse>(
      `/stock/recipes/${id}`,
      requestOptions,
    );
    forgetStockDocumentUpdatedAt('recipe', id);
    return response;
  },

  recalculateProductions: async (
    id: string,
    data: RecipeRecalculateProductionsRequest,
    options?: StockDocumentMutationOptions,
  ): Promise<RecipeRecalculateProductionsResponse> => {
    const requestOptions = options?.idempotencyKey
      ? { idempotencyKey: options.idempotencyKey }
      : undefined;
    return api.webAdmin.post<RecipeRecalculateProductionsResponse>(
      `/stock/recipes/${id}/recalculate-productions`,
      data,
      requestOptions,
    );
  },

  preview: async (data: RecipePreviewRequest): Promise<RecipePreviewResponse> => {
    return api.webAdmin.post<RecipePreviewResponse>('/stock/recipes/preview', data, {
      autoIdempotencyKey: false,
    });
  },

  flattenActiveRecipePerUnit: async (productId: string): Promise<RecipeFlattenResponse> => {
    return api.webAdmin.get<RecipeFlattenResponse>(`/stock/recipes/flatten/${productId}`);
  },

  productsWithRecipes: async (): Promise<ProductsWithRecipesResponse> => {
    return api.webAdmin.get<ProductsWithRecipesResponse>('/stock/recipes/products-with-recipes');
  },

  categoryGroups: async (): Promise<RecipeCategoryGroupsResponse> => {
    return api.webAdmin.get<RecipeCategoryGroupsResponse>('/stock/recipes/category-groups');
  },

  usedIn: async (recipeId: string): Promise<RecipeUsedInResponse> => {
    return api.webAdmin.get<RecipeUsedInResponse>(`/stock/recipes/${recipeId}/used-in`);
  },
});
